import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  IconButton,
  InputBase,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  Grid,
  MenuItem,
  Select,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import createInstance from "../../../api/axiosinstance";
import Toast from "../../../utils/toast";

const aiDishaApi = createInstance("DISHA");

const TP_interviewermgmt = () => {
  const [interviewers, setInterviewers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loadingId, setLoadingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedInterviewer, setSelectedInterviewer] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchInterviewers = async () => {
    try {
      const response = await aiDishaApi.get("/get-all-interviewer");
      setInterviewers(response.data.active_users || []);
    } catch (error) {
      console.error("Error fetching interviewers:", error);
      Toast.error("Failed to fetch interviewers");
    }
  };

  useEffect(() => {
    fetchInterviewers();
  }, []);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusChange = async (userId, newStatus) => {
    setLoadingId(userId);
    try {
      await aiDishaApi.patch(
        `/update-interviewer-status?user_id=${userId}&status=${newStatus}`
      );
      Toast.success(`Status updated to "${newStatus}"`);
      await fetchInterviewers();
    } catch (error) {
      console.error("Error updating status:", error);
      Toast.error("Failed to update status");
    } finally {
      setLoadingId(null);
    }
  };

  const handleView = (interviewer) => {
    setSelectedInterviewer(interviewer);
    setIsEditMode(false);
    setModalOpen(true);
  };

  const handleEdit = (interviewer) => {
    setSelectedInterviewer({
      ...interviewer,
      address: interviewer.address || {
        H_No: "",
        city: "",
        state: "",
        country: "",
      },
    });
    setIsEditMode(true);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedInterviewer(null);
  };

  const handleInputChange = (field, value) => {
    setSelectedInterviewer((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddressChange = (field, value) => {
    setSelectedInterviewer((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { user_id, name, email, mobile, Date_of_birth, address } =
        selectedInterviewer;

      await aiDishaApi.put(`/update-interviewer-profile?user_id=${user_id}`, {
        name,
        email,
        mobile,
        Date_of_birth,
        address,
      });

      Toast.success("Profile updated successfully");
      setModalOpen(false);
      await fetchInterviewers();
    } catch (error) {
      console.error("Error updating profile:", error);
      Toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const filteredInterviewers = interviewers.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedInterviewers = filteredInterviewers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const interviewerHeaders = [
    "User ID",
    "Name",
    "Email",
    "Mobile",
    "Date of Birth",
    "Role",
    "Status",
    "Activation Status",
    "Action",
  ];

  return (
    <div
      style={{
        height: "calc(100% - 6%)",
        overflow: "scroll",
        scrollbarWidth: "thin",
        msOverflowStyle: "none",
      }}
    >
      {/* Search Bar */}
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        sx={{
          marginTop: "auto",
          alignItems: "center",
          borderRadius: "10px",
          padding: "5% 1% 0% 1%",
        }}
      >
        <Box
          gridColumn="span 12"
          sx={{ marginTop: "auto", textAlign: "left", marginBottom: "2%" }}
        >
          <Paper
            sx={{
              p: "2px 4px",
              display: "flex",
              width: "75%",
              borderRadius: 2,
              backgroundColor: "#033538",
            }}
          >
            <IconButton
              type="button"
              sx={{ p: "8px", color: "white" }}
              aria-label="search"
            >
              <SearchIcon />
            </IconButton>
            <InputBase
              sx={{ ml: 1, flex: 1, fontFamily: "Poppins", color: "white" }}
              placeholder="Search by Interviewer Name"
              inputProps={{ "aria-label": "search interviewer" }}
              value={searchTerm}
              onChange={handleSearch}
            />
          </Paper>
        </Box>
      </Box>

      {/* Data Table */}
      <Box xs={1} sx={{ backgroundColor: "#33333" }}>
        <Grid container spacing={1} sx={{ justifyContent: "center" }}>
          <Grid item xs={10}>
            <TableContainer
              component={Paper}
              sx={{
                marginTop: "25px",
                borderRadius: "10px",
                marginLeft: "-55px",
              }}
            >
              <Table size="small">
                <TableHead sx={{ backgroundColor: "#006A71" }}>
                  <TableRow>
                    {interviewerHeaders.map((header, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          color: "white",
                          borderBottom: 0,
                          fontWeight: "bold",
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody sx={{ backgroundColor: "#033538" }}>
                  {paginatedInterviewers.length > 0 ? (
                    paginatedInterviewers.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ color: "white" }}>
                          {item.user_id}
                        </TableCell>
                        <TableCell sx={{ color: "white" }}>
                          {item.name}
                        </TableCell>
                        <TableCell sx={{ color: "white" }}>
                          {item.email}
                        </TableCell>
                        <TableCell sx={{ color: "white" }}>
                          {item.mobile}
                        </TableCell>
                        <TableCell sx={{ color: "white" }}>
                          {item.Date_of_birth
                            ? new Date(item.Date_of_birth).toLocaleDateString()
                            : "-"}
                        </TableCell>
                        <TableCell sx={{ color: "white" }}>
                          {item.role}
                        </TableCell>
                        <TableCell sx={{ color: "white" }}>
                          {item.status}
                        </TableCell>
                        <TableCell sx={{ color: "white" }}>
                          {loadingId === item.user_id ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : (
                            <Select
                              size="small"
                              value={item.activation_status || "Pending"}
                              onChange={(e) =>
                                handleStatusChange(item.user_id, e.target.value)
                              }
                              sx={{
                                backgroundColor: "#004D4D",
                                color: "white",
                                "& .MuiSelect-icon": { color: "white" },
                              }}
                            >
                              <MenuItem sx={{ color: "black" }} value="Pending">
                                Pending
                              </MenuItem>
                              <MenuItem
                                sx={{ color: "black" }}
                                value="Completed"
                              >
                                Completed
                              </MenuItem>
                            </Select>
                          )}
                        </TableCell>

                        {/* Action Buttons */}
                        <TableCell sx={{ color: "white" }}>
                          <IconButton
                            color="primary"
                            onClick={() => handleView(item)}
                          >
                            <VisibilityIcon sx={{ color: "white" }} />
                          </IconButton>
                          <IconButton
                            color="secondary"
                            onClick={() => handleEdit(item)}
                          >
                            <EditIcon sx={{ color: "white" }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={interviewerHeaders.length}
                        sx={{ textAlign: "center", color: "white" }}
                      >
                        No Data Available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={filteredInterviewers.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{
                backgroundColor: "#006A71",
                color: "white",
                marginLeft: "-55px",
                width: "100%",
                borderRadius: "0 0 10px 10px",
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* View/Edit Modal */}
      {selectedInterviewer && (
        <Dialog
          open={modalOpen}
          onClose={handleModalClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            style: { backgroundColor: "white", borderRadius: "8px" },
          }}
        >
          <div
            className="modal-content"
            style={{
              backgroundColor: "white",
              color: "#033538",
              padding: "10px",
            }}
          >
            <div className="modal-header border-0 d-flex justify-content-between align-items-center">
              <h5
                className="modal-title"
                style={{
                  color: "#033538",
                  fontFamily: "Poppins",
                  fontWeight: "600",
                }}
              >
                {isEditMode
                  ? "Edit Interviewer Profile"
                  : "View Interviewer Profile"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleModalClose}
                aria-label="Close"
              ></button>
            </div>

            <div className="modal-body" style={{ padding: "12px" }}>
              <form>
                <div className="row" style={{ margin: "0 -12px" }}>
                  {/* User ID - Read Only */}
                  <div
                    className="col-sm-3"
                    style={{ padding: "0 12px", marginBottom: "1rem" }}
                  >
                    <label
                      style={{
                        fontFamily: "Poppins",
                        fontSize: "12px",
                        color: "#033538",
                      }}
                    >
                      User ID
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={selectedInterviewer.user_id || ""}
                      readOnly
                      style={{
                        backgroundColor: "#033538",
                        color: "white",
                        border: "none",
                      }}
                    />
                  </div>

                  {/* Name */}
                  <div
                    className="col-sm-3"
                    style={{ padding: "0 12px", marginBottom: "1rem" }}
                  >
                    <label
                      style={{
                        fontFamily: "Poppins",
                        fontSize: "12px",
                        color: "#033538",
                      }}
                    >
                      Full Name
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Enter Name"
                      value={selectedInterviewer.name || ""}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      disabled={!isEditMode}
                      style={{
                        backgroundColor: "#033538",
                        color: "white",
                        border: "none",
                      }}
                    />
                  </div>
                
                  

                  {/* Date of Birth */}
                  <div
                    className="col-sm-4"
                    style={{ padding: "0 12px", marginBottom: "1rem" }}
                  >
                    <label
                      style={{
                        fontFamily: "Poppins",
                        fontSize: "12px",
                        color: "#033538",
                      }}
                    >
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={
                        selectedInterviewer.Date_of_birth
                          ? selectedInterviewer.Date_of_birth.split("T")[0]
                          : ""
                      }
                      onChange={(e) =>
                        handleInputChange("Date_of_birth", e.target.value)
                      }
                      disabled={!isEditMode}
                      style={{
                        backgroundColor: "#033538",
                        color: "white",
                        border: "none",
                      }}
                    />
                  </div>
                </div>

                <div className="row" style={{ margin: "0 -12px" }}>
                    {/* Email */}
                  <div
                    className="col-sm-5"
                    style={{ padding: "0 12px", marginBottom: "1rem" }}
                  >
                    <label
                      style={{
                        fontFamily: "Poppins",
                        fontSize: "12px",
                        color: "#033538",
                      }}
                    >
                      Email ID
                    </label>
                    <input
                      type="email"
                      className="form-control form-control-sm"
                      value={selectedInterviewer.email || ""}
                      onChange={(e) =>
                        handleInputChange("email", e.target.value)
                      }
                      disabled={!isEditMode}
                      style={{
                        backgroundColor: "#033538",
                        color: "white",
                        border: "none",
                      }}
                    />
                  </div>
                  {/* Mobile */}
                  <div
                    className="col-sm-5"
                    style={{ padding: "0 12px", marginBottom: "1rem" }}
                  >
                    <label
                      style={{
                        fontFamily: "Poppins",
                        fontSize: "12px",
                        color: "#033538",
                      }}
                    >
                      Mobile
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={selectedInterviewer.mobile || ""}
                      onChange={(e) =>
                        handleInputChange("mobile", e.target.value)
                      }
                      disabled={!isEditMode}
                      style={{
                        backgroundColor: "#033538",
                        color: "white",
                        border: "none",
                      }}
                    />
                  </div>
                </div>

                {/* Address Fields */}
                <div className="row" style={{ margin: "0 -12px" }}>
                  <div
                    className="col-12"
                    style={{ padding: "0 12px", marginBottom: "1rem" }}
                  >
                    <label
                      style={{
                        fontFamily: "Poppins",
                        fontSize: "12px",
                        color: "#033538",
                      }}
                    >
                      Address
                    </label>
                  </div>
                  {["H_No", "city", "state", "country"].map((field) => (
                    <div
                      key={field}
                      className="col-sm-5"
                      style={{ padding: "0 12px", marginBottom: "1rem" }}
                    >
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder={field.replace("_", " ")}
                        value={selectedInterviewer.address?.[field] || ""}
                        onChange={(e) =>
                          handleAddressChange(field, e.target.value)
                        }
                        disabled={!isEditMode}
                        style={{
                          backgroundColor: "#033538",
                          color: "white",
                          border: "none",
                        }}
                      />
                    </div>
                  ))}
                </div>
              </form>
            </div>

            <div className="modal-footer border-0 d-flex justify-content-end gap-3">
              <button
                type="button"
                className="btn btn-sm rounded-2 fw-medium"
                onClick={handleModalClose}
                style={{
                  color: "#033538",
                  width: "120px",
                  border: "1px solid #033538",
                }}
              >
                Cancel
              </button>

              {isEditMode && (
                <button
                  type="button"
                  className="btn btn-sm rounded-2 fw-medium"
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    backgroundColor: "#033538",
                    color: "#E8E8E8",
                    width: "120px",
                  }}
                >
                  {saving ? (
                    <div
                      className="spinner-border spinner-border-sm text-light"
                      role="status"
                    >
                      <span className="visually-hidden">Saving...</span>
                    </div>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              )}
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default TP_interviewermgmt;
